import { getSharePipe } from '@context/useShareContext/methods/shareInitialization';
import getLocalizationByArgs from '@helpers/getLocalizationByArgs';
import { ServiceDirectorySettings } from '@onlog-public/additional-services-types';
import {
  initOptions,
  optionsState$,
} from '@pages/AdditionalServices/containers/additionServicesOptionsContext';
import {
  serviceCalculationBus$,
  serviceContext$,
  serviceLoadingBus$,
} from '@pages/AdditionalServices/containers/bus';
import { clone } from '@pages/AdditionalServices/containers/methods/clone';
import initPropertyValueOptions from '@pages/AdditionalServices/containers/methods/initPropertyValueOptions';
import { resetSelectedDirectory } from '@pages/AdditionalServices/containers/methods/resetSelectedDirectory';
import { setSelectedDirectory } from '@pages/AdditionalServices/containers/methods/setSelectedDirectory';
import { calculateServicesSubscriber } from '@pages/AdditionalServices/containers/subscribers/calculateServicesSubscriber';
import { directoryLoadingSubscriber } from '@pages/AdditionalServices/containers/subscribers/directoryLoadingSubscriber';
import { isLastLevelSelectedCheckSubscriber } from '@pages/AdditionalServices/containers/subscribers/isLastLevelSelectedCheckSubscriber';
import { makeDirectoryFieldsSubscriber } from '@pages/AdditionalServices/containers/subscribers/makeDirectoryFieldsSubscriber';
import makeDirectoryPropertiesSubscriber from '@pages/AdditionalServices/containers/subscribers/makeDirectoryPropertiesSubscriber';
import { serviceLoadingSubscriber } from '@pages/AdditionalServices/containers/subscribers/serviceLoadingSubscriber';
import { AdditionServicesState } from '@pages/AdditionalServices/containers/types';
import {
  WidgetTab,
  getTabsPipe,
  pushTabs,
} from '@pages/AdditionalServices/containers/widgetTabsContext';
import { getOrderPipe } from '@pages/Basket/useBasket';
import { servicesDirectoriesService } from '@services/requests/servicesService/servicesDirectoriesService';
import getEnv from '@settings/getEnv';
import { checkEnvAvailability } from '@settings/getEnv/helpers';
import { Subject, bufferTime, distinctUntilChanged, filter, first, withLatestFrom } from 'rxjs';

import { getServicePipe } from '../additionServicesContext';
import { calculateFieldsSubscriber } from '../subscribers/calculateFieldsSubscriber';
import { restoreAdditionalServiceFromStorageSubscriber } from './restore/restoreAdditionalServiceFromStorageSubscriber';
import { restoreAdditionalServiceSubscriber } from './restore/restoreAdditionalServiceSubscriber';

// Subject восстановления состояния доп. услуг через шаринг или редактирование продукта
const restoreSubject$ = new Subject<{ type: 'order' | 'share'; data: any }>();

/**
 * getLocalStorageDataToStoreInCache возвращает данные, которые будут сохраняться в кэш LocalStorage
 * и извлекаться при восстановлении стейта. Данные, помещаемые сюда должны актуализироваться в
 * процессе работы стейта. Данные используются только для первоначального открытия страницы.
 *
 * @param state
 */
const getLocalStorageDataToStoreInCache = (state: AdditionServicesState) => {
  const { RootServices } = state;

  return {
    RootServices,
  };
};

/**
 * sortDirectories реализует функцию сортировки директорий в табах.
 * Если для директорий задан одинаковый приоритет, то они сортируются по ID.
 * @param a
 * @param b
 */
export const sortDirectories = (a: WidgetTab, b: WidgetTab) => {
  if (a.data.settings.PositionInTabs === b.data.settings.PositionInTabs) {
    return parseInt(a.data.id) > parseInt(b.data.id) ? 1 : -1;
  }

  return a.data.settings.PositionInTabs > b.data.settings.PositionInTabs ? 1 : -1;
};

/**
 * Инициализация данных стейта. Загружает данные, а так же добавляет необходимую логику.
 */
export const initialization = async (langID: string, currencyID: string) => {
  const { REACT_APP_FEATURE_ADDITIONAL_SERVICES_ENABLED } = getEnv();
  const isAdditionalServicesEnabled = checkEnvAvailability(
    REACT_APP_FEATURE_ADDITIONAL_SERVICES_ENABLED
  );

  if (!isAdditionalServicesEnabled) {
    return;
  }

  const rootServices = (await servicesDirectoriesService().GetServicesByPath()).filter(
    (s) => s.is_active
  );

  const state = clone(serviceContext$.getValue());
  if (Object.keys(state.ServicesPath).length > 0) {
    const selection = rootServices.find((d) => d.id === state.ServicesPath['0'].id);
    if (!selection) {
      state.ServicesPath = {};
    }
  }

  serviceContext$.next({
    ...state,
    RootServices: rootServices,
    CurrencyID: currencyID,
    LanguageID: langID,
  });

  pushTabs(
    rootServices
      .filter((s) => !s.settings.LanguagesToHide.includes(langID))
      .map((s) => ({
        label: getLocalizationByArgs(langID, s.name, s.localized_names),
        data: s,
        type: 'service' as WidgetTab['type'],
      }))
      .slice()
      .sort(sortDirectories)
  );

  // Подключаем стейт опций для полей
  initOptions(langID);

  // Подключаем стейт опций для свойств услуг
  initPropertyValueOptions(langID);

  // Подписка на изменения данных в выбранной вкладке
  getTabsPipe().subscribe({
    next: (tabs) => {
      if (tabs.ActiveTabData.type !== 'service') {
        return resetSelectedDirectory();
      }

      setSelectedDirectory(0, {
        ...tabs.ActiveTabData.data,
        settings: ServiceDirectorySettings.RestoreSettings(tabs.ActiveTabData.data.settings),
      });
    },
  });

  //Подписка автоматически подгружает новые уровни для директорий
  // при изменении выбранных в виджете.
  serviceContext$.pipe().subscribe({
    next: directoryLoadingSubscriber(serviceContext$),
  });

  // Подписчик автоматически вычисляет состояние флага IsLastLevelSelected
  // при изменении списка выбранных директорий.
  serviceContext$.pipe(bufferTime(50)).subscribe({
    next: isLastLevelSelectedCheckSubscriber(serviceContext$),
  });

  // Подписчик вычисляет состояние полей в стейте. Если выбрана крайняя директория,
  // то генерируется список полей для вывода. Для каждого поля инициализируется стейт,
  // а так же инициализируется запрос дополнительных данных.
  serviceContext$.pipe(bufferTime(50)).subscribe({
    next: makeDirectoryFieldsSubscriber(serviceContext$),
  });

  optionsState$.pipe(bufferTime(50)).subscribe({
    next: calculateFieldsSubscriber(serviceContext$),
  });

  // Подписчик вычисляет все отображаемые свойства выбранных директорий и формирует
  // по ним полноценный стейт значений. Для свойства формирует стейт для хранения
  // его значений.
  serviceContext$.pipe(bufferTime(50)).subscribe({
    next: makeDirectoryPropertiesSubscriber(serviceContext$),
  });

  /**
   * Сохранение данных дополнительных услуг в кэш в localStorage
   */
  serviceContext$.pipe().subscribe({
    next: (state) => {
      localStorage.setItem(
        '__ONLOG_A_SERVICES_DATA__',
        JSON.stringify(getLocalStorageDataToStoreInCache(state))
      );
    },
  });

  // Подписка на загрузку услуг для выбранных директорий
  serviceLoadingBus$.pipe(bufferTime(100)).subscribe({
    next: serviceLoadingSubscriber(serviceContext$, serviceCalculationBus$, optionsState$),
  });

  // Обработка запросов расчета стоимости услуг. Запускается
  // с перерывами. При расчете используется идентификатор.
  // Значения пишутся только для последнего идентификатора.
  serviceCalculationBus$.pipe(bufferTime(500)).subscribe({
    next: calculateServicesSubscriber(serviceContext$),
  });

  //   serviceContext$.pipe().subscribe({
  //     next: (state) => {
  //       if (Object.keys(state.ServicesPath).length === 0) {
  //         onChangeTab(0);
  //       }
  //     },
  //   });

  // Подписка на изменения активного таба. Пропускает выполнение если активный таб
  // не изменился, а также типы табов не являющееся доп. усулугами.
  // Меняет флаг восстановления услуги на активный.
  getTabsPipe()
    .pipe(distinctUntilChanged((prev, curr) => prev.ActiveTab === curr.ActiveTab))
    .subscribe({
      next: (state) => {
        if (state.ActiveTabData.type !== 'service') {
          return;
        }

        serviceContext$.next({
          ...serviceContext$.getValue(),
          IsRestoringState: true,
        });
      },
    });

  // Подписка на изменения состояние шаринга с последними значениями стейтов табов и сервисов.
  // Пропускает выполнение если происходит инциализация шаринга из ссылки или хранилища, или же
  // при условии что активный таб является доп. услугой.
  // Выключает флаг восстановления услуги.
  getSharePipe()
    .pipe(withLatestFrom(getTabsPipe(), getServicePipe()))
    .pipe(
      filter(
        ([share, tabs]) =>
          (!share.IsShareLoading && !share.IsStorageShareLoading) ||
          tabs.ActiveTabData.type !== 'service'
      )
    )
    .subscribe({
      next: ([share, tabs, service]) => {
        if (
          (!share.ShareData && !share.StorageShareData) ||
          tabs.ActiveTabData.type !== 'service'
        ) {
          serviceContext$.next({
            ...service,
            IsRestoringState: false,
          });
        }
      },
    });

  // Подписка на стейт шаринг для запуска восстановления стейта доп. услуг из хранилища браузера.
  // Пропускает восстановление если ссылки на данные для восстановления между выполнениями не изменились
  // Не выполняется если присутствует восстановление через url параметр share или отсутствует
  // данные для восстановления из хранилища.
  // Запускает восстановление.
  getSharePipe()
    .pipe(
      distinctUntilChanged(
        (prev, curr) => prev.StorageShareShortLink === curr.StorageShareShortLink
      )
    )
    .subscribe({
      next: (state) => {
        if ((!state.StorageShareShortLink && !state.StorageShareData) || !!state.ShareType) {
          return;
        }
        restoreAdditionalServiceFromStorageSubscriber(state.StorageShareData, langID, currencyID);
      },
    });

  // Подписка на subject восстановления для запуска восстановления стейта доп. услуг.
  // Во время выполения получает последние данных из стейтов шаринга и заказа.
  // Выполняется один при условии существования одно из или ни одного варианта восстановления.
  // Запускает восстановление.
  restoreSubject$
    .pipe(withLatestFrom(getOrderPipe(), getSharePipe(), getServicePipe(), getTabsPipe()))
    .pipe(
      filter(
        ([_, __, ___, service, tabs]) =>
          service.IsRestoringState && tabs.ActiveTabData.type === 'service'
      )
    )
    .pipe(
      first(([state, order, share]) => {
        return (
          (!!order.editProduct && state.type === 'order') ||
          (!!share.ShareType && state.type === 'share') ||
          (!order.editProduct && !order.isLoading && !share.ShareType && !share.IsShareLoading)
        );
      })
    )
    .subscribe({
      next: ([state, order, share]) => {
        const isNoRestoreExist =
          !order.editProduct && !order.isLoading && !share.ShareType && !share.IsShareLoading;

        restoreAdditionalServiceSubscriber(state, isNoRestoreExist, langID, currencyID);
      },
    });

  //   getTabsPipe()
  //     .pipe(withLatestFrom(getServicePipe(), getSharePipe()))
  //     .pipe(skipWhile(([_, service, share]) => service.IsRestoringState && !!share.ShareData))
  //     .subscribe({
  //       next: ([tabs]) => {
  //         if (tabs.ActiveTabData.type !== 'service') {
  //           return resetSelectedDirectory();
  //         }

  //         const savedServiceRestore = getSavedServiceRestoreByServiceId(
  //           tabs.ActiveTabData.data?.id ?? ''
  //         );

  //         if (!savedServiceRestore) {
  //           return;
  //         }

  //         serviceContext$.next({
  //           ...serviceContext$.getValue(),
  //           IsRestoringState: true,
  //         });

  //         restoreAdditionalServiceFromStorageSubscriber(savedServiceRestore, langID, currencyID);
  //       },
  //     });

  // Подписчик пулит в subject восстановления данные из заказа и тип
  getOrderPipe()
    .pipe()
    .subscribe({
      next: (order) => {
        if (order.isLoading) {
          return;
        }

        restoreSubject$.next({ type: 'order', data: order });
      },
    });

  // Подписчик пулит в subject восстановления данные из шаринга и тип
  getSharePipe()
    .pipe()
    .subscribe({
      next: (share) => {
        if (share.IsShareLoading) {
          return;
        }

        restoreSubject$.next({ type: 'share', data: share });
      },
    });
};
